///
/// EarthBrowser
///
/// Created by Matt Giger on 10/25/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Terrain lid of the earthquake cutout: a heightfield over the block
/// footprint sampled from the elevation tiles, with skirts down to sea level
/// so the slab reads as solid. Built in the block frame (x along strike,
/// y up, z across; km, y = 0 at sea level).
///

import { fromBlockXZ, localToLatLon, niceStep, seaFloorExaggeration, terrainExaggeration, type CutoutLayout } from "./cutoutLayout"
import { type CutoutElevation } from "./CutoutElevation"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type cube } from "@stovokor/math/cube"
import { trig } from "@stovokor/math/math"
import { type matrix } from "@stovokor/math/matrix"
import { type sphere } from "@stovokor/math/sphere"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"

const cGridAlong = 128

export class CutoutTerrainNode extends NodeBase {
	private readonly terrainRenderer: CutoutTerrainRenderer
	/** Integer vertical exaggeration applied to land */
	public readonly exaggeration: number
	/**
	 * Exaggeration applied below sea level. Capped so the exaggerated sea
	 * floor stays in the top quarter of the box and never dives below the
	 * shallow hypocenters it is supposed to sit above
	 */
	public readonly seaExaggeration: number
	/** Highest sampled elevation in the footprint, km (unexaggerated) */
	public readonly maxElevKm: number
	/** Deepest sampled sea floor in the footprint, km (positive, unexaggerated); 0 without bathymetry */
	public readonly maxDepthKm: number
	/** Tsunami travel time to the nearest block edge over water, seconds; undefined when no field was built */
	public readonly tsunamiEdgeArrivalSec?: number
	/** Isochron spacing drawn on the sea floor, minutes */
	public readonly isochronMin: number = 10

	/**
	 * @param tsunamiSource - block-frame x/z of the epicenter; when it lies
	 * over water a shallow-water travel-time field is baked into the lid
	 */
	constructor(context: StovokorContext, layout: CutoutLayout, elevation: CutoutElevation, lat0: number, lon0: number, tsunamiSource?: { x: number; z: number }) {
		super()
		const geometry = new CutoutTerrainGeometry(context, layout, elevation, lat0, lon0, tsunamiSource)
		this.exaggeration = geometry.exaggeration
		this.seaExaggeration = geometry.seaExaggeration
		this.maxElevKm = geometry.maxElevKm
		this.maxDepthKm = geometry.maxDepthKm
		this.tsunamiEdgeArrivalSec = geometry.tsunamiEdgeArrivalSec
		this.terrainRenderer = new CutoutTerrainRenderer(context, geometry)
		if(this.tsunamiEdgeArrivalSec !== undefined) {
			// A few isochrons between the epicenter and the nearest edge
			this.isochronMin = Math.max(1, niceStep(this.tsunamiEdgeArrivalSec / 60 / 4))
			this.terrainRenderer.isochronSec.value = this.isochronMin * 60
		}
		this.terrainRenderer.exaggeration.value = this.exaggeration
		this.terrainRenderer.seaExaggeration.value = this.seaExaggeration
		this.terrainRenderer.elevMin.value = geometry.minLandElevKm * 1000
		this.terrainRenderer.elevMax.value = geometry.maxElevKm * 1000
		this.renderer = this.terrainRenderer
		// After the walls and shadows in the blended pass so it composites over them
		this.renderOrder = 2
	}

	/** 0..1 rise-out animation */
	public set reveal(value: number) {
		this.terrainRenderer.reveal.value = value
	}

	/** 0..1 fade multiplier */
	public set opacity(value: number) {
		this.terrainRenderer.opacity = value
	}

	/** Seconds since the mainshock for the tsunami front (negative = not yet) */
	public setTsunamiClock(seconds: number): void {
		this.terrainRenderer.tsunamiClock.value = this.tsunamiEdgeArrivalSec === undefined ? -1 : seconds
	}
}

class CutoutTerrainRenderer extends Renderer {
	public modelViewProjection = new UniformDynamic<matrix>()
	public exaggeration = new UniformDynamic<number>(1)
	public seaExaggeration = new UniformDynamic<number>(1)
	public reveal = new UniformDynamic<number>(0)
	public lidAlpha = new UniformDynamic<number>(1)
	public elevMin = new UniformDynamic<number>(0)
	public elevMax = new UniformDynamic<number>(1000)
	public tsunamiClock = new UniformDynamic<number>(-1)
	public isochronSec = new UniformDynamic<number>(600)
	/** Overall fade multiplier applied to the view-dependent alpha */
	public opacity = 1

	constructor(context: StovokorContext, geometry: Geometry) {
		// Blended so the interior shows through from above, but still writes
		// depth so it occludes the walls and spheres behind it correctly
		const transparentMaterial: Material = { transparency: true, available: true }
		super(context, new ShaderSourceDefinition("cutout_terrain"), geometry, [transparentMaterial])
		this.drawState.depthWrite = true
		// Skirts are seen from outside and the lid from above; the slab can
		// also be viewed edge-on from below the sea-level plane
		this.drawState.cullFace = false
	}

	protected stateUpdate(state: RenderState): void {
		this.modelViewProjection.value = state.modelViewProjection
		// Camera elevation over the block: 1 straight above, 0 edge-on
		const camLocal = state.modelMatrix.invert().multVec3(state.camera.position, 1)
		const len = camLocal.length
		const cosTilt = len > 0 ? Math.max(0, camLocal.y / len) : 1
		const t = smoothstep(0.2, 0.75, cosTilt)
		this.lidAlpha.value = (0.94 + (0.42 - 0.94) * t) * this.opacity
	}
}

function smoothstep(e0: number, e1: number, x: number): number {
	const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)))
	return t * t * (3 - 2 * t)
}

class CutoutTerrainGeometry implements Geometry {
	public position: VertexAttribute
	public normal: VertexAttribute
	public kind: VertexAttribute
	/** Tsunami travel time per vertex, seconds (-1 on land or without a source) */
	public travel: VertexAttribute
	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer
	public mode: DrawMode = DrawMode.Triangles
	public bounds: cube
	public boundingSphere: sphere
	public available: boolean = true
	public readonly exaggeration: number
	public readonly seaExaggeration: number
	public readonly maxElevKm: number
	public readonly maxDepthKm: number
	/** Lowest land (non-zero) sample, km; the tint ramp starts here */
	public readonly minLandElevKm: number
	public readonly tsunamiEdgeArrivalSec?: number

	constructor(context: StovokorContext, layout: CutoutLayout, elevation: CutoutElevation, lat0: number, lon0: number, tsunamiSource?: { x: number; z: number }) {
		this.position = new VertexAttribute(context, 3)
		this.normal = new VertexAttribute(context, 3)
		this.kind = new VertexAttribute(context, 1)
		this.travel = new VertexAttribute(context, 1)
		this.indexBuffer = new IndexBuffer(context)
		this.vertexBuffer = new VertexBuffer(context, [this.position, this.normal, this.kind, this.travel])

		const nx = cGridAlong
		const nz = Math.max(32, Math.round(cGridAlong * layout.widthKm / layout.lengthKm))
		const dx = layout.lengthKm / (nx - 1)
		const dz = layout.widthKm / (nz - 1)
		const halfX = layout.lengthKm / 2
		const halfZ = layout.widthKm / 2

		// Sample signed heights (km) for every grid vertex
		const heights = new Float32Array(nx * nz)
		let maxElevKm = 0
		let minHeightKm = 0
		let minLandElevKm = Infinity
		for(let j = 0; j < nz; j++) {
			const z = -halfZ + j * dz
			for(let i = 0; i < nx; i++) {
				const x = -halfX + i * dx
				const local = fromBlockXZ(x, z, layout.strike)
				const geo = localToLatLon(local.east + layout.centerEast, local.north + layout.centerNorth, lat0, lon0)
				const h = elevation.sampleMeters(geo.lat * trig.rad, geo.lon * trig.rad) / 1000
				heights[j * nx + i] = h
				if(h > maxElevKm) maxElevKm = h
				if(h < minHeightKm) minHeightKm = h
				if(h > 0 && h < minLandElevKm) minLandElevKm = h
			}
		}
		this.maxElevKm = maxElevKm
		this.maxDepthKm = -minHeightKm
		this.minLandElevKm = Number.isFinite(minLandElevKm) ? minLandElevKm : 0
		this.exaggeration = terrainExaggeration(layout, maxElevKm, this.minLandElevKm)
		this.seaExaggeration = seaFloorExaggeration(layout, this.exaggeration, this.maxDepthKm)
		const exag = this.exaggeration
		const seaExag = this.seaExaggeration
		const lift = (h: number): number => h >= 0 ? h * exag : h * seaExag

		// Shallow-water tsunami travel time from the epicenter over the sea floor
		let travel: Float32Array | undefined
		if(tsunamiSource && this.maxDepthKm > 0.05) {
			const si = Math.round((tsunamiSource.x + halfX) / dx)
			const sj = Math.round((tsunamiSource.z + halfZ) / dz)
			if(si >= 0 && si < nx && sj >= 0 && sj < nz && heights[sj * nx + si] <= 0) {
				travel = tsunamiTravelTime(heights, nx, nz, dx, dz, si, sj)
				let edge = Infinity
				for(let i = 0; i < nx; i++) {
					edge = Math.min(edge, travel[i], travel[(nz - 1) * nx + i])
				}
				for(let j = 0; j < nz; j++) {
					edge = Math.min(edge, travel[j * nx], travel[j * nx + nx - 1])
				}
				this.tsunamiEdgeArrivalSec = Number.isFinite(edge) ? edge : undefined
			}
		}

		// Lid vertices with normals at full exaggeration (central differences)
		const at = (i: number, j: number): number => heights[Math.min(nz - 1, Math.max(0, j)) * nx + Math.min(nx - 1, Math.max(0, i))]
		for(let j = 0; j < nz; j++) {
			const z = -halfZ + j * dz
			for(let i = 0; i < nx; i++) {
				const x = -halfX + i * dx
				const h = at(i, j)
				const dhdx = (lift(at(i + 1, j)) - lift(at(i - 1, j))) / (2 * dx)
				const dhdz = (lift(at(i, j + 1)) - lift(at(i, j - 1))) / (2 * dz)
				const len = Math.hypot(dhdx, 1, dhdz)
				this.position.addPos(x, h, z)
				// Bounds must cover the exaggerated lid for culling
				this.position.bounds.update(x, lift(h), z)
				this.normal.add(-dhdx / len, 1 / len, -dhdz / len)
				this.kind.add(0)
				const t = travel ? travel[j * nx + i] : Infinity
				this.travel.add(Number.isFinite(t) ? t : -1)
			}
		}
		for(let j = 0; j < nz - 1; j++) {
			for(let i = 0; i < nx - 1; i++) {
				const a = j * nx + i
				const b = a + 1
				const c = a + nx + 1
				const d = a + nx
				this.indexBuffer.quad(a, d, c, b)
			}
		}

		// Skirts: perimeter edge between the lid and sea level. Above sea
		// level that is the cut rock face; below it, the water column
		// between the sea surface and the sea floor
		const addSkirt = (i0: number, j0: number, i1: number, j1: number, nxN: number, nzN: number): void => {
			const x0 = -halfX + i0 * dx, z0 = -halfZ + j0 * dz
			const x1 = -halfX + i1 * dx, z1 = -halfZ + j1 * dz
			const h0 = at(i0, j0)
			const h1 = at(i1, j1)
			const water = h0 <= 0 && h1 <= 0
			const base = this.position.count
			this.position.addPos(x0, h0, z0)
			this.position.addPos(x1, h1, z1)
			this.position.addPos(x1, 0, z1)
			this.position.addPos(x0, 0, z0)
			for(let k = 0; k < 4; k++) {
				this.normal.add(nxN, 0, nzN)
				this.kind.add(water ? 2 : 1)
				this.travel.add(-1)
			}
			this.indexBuffer.quad(base, base + 1, base + 2, base + 3)
		}
		for(let i = 0; i < nx - 1; i++) {
			addSkirt(i, 0, i + 1, 0, 0, -1)
			addSkirt(i, nz - 1, i + 1, nz - 1, 0, 1)
		}
		for(let j = 0; j < nz - 1; j++) {
			addSkirt(0, j, 0, j + 1, -1, 0)
			addSkirt(nx - 1, j, nx - 1, j + 1, 1, 0)
		}

		this.bounds = this.position.bounds
		this.boundingSphere = this.bounds.sphere
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.indexBuffer.destroy()
	}
}

/**
 * Dijkstra over the lid grid with the shallow-water wave speed √(g·h)
 * (h = water depth, floored at 20 m so the shoreline doesn't stall the
 * front); land cells are walls. Returns seconds from the source per cell,
 * Infinity where unreachable
 */
function tsunamiTravelTime(heights: Float32Array, nx: number, nz: number, dxKm: number, dzKm: number, si: number, sj: number): Float32Array {
	const n = nx * nz
	const time = new Float32Array(n).fill(Infinity)
	const done = new Uint8Array(n)
	const speed = (idx: number): number => {
		const depthM = Math.max(-heights[idx] * 1000, 20)
		return Math.sqrt(9.81 * depthM)	// m/s
	}
	// Binary heap of [time, index]
	const heapT: number[] = []
	const heapI: number[] = []
	const push = (t: number, i: number): void => {
		heapT.push(t); heapI.push(i)
		let k = heapT.length - 1
		while(k > 0) {
			const p = (k - 1) >> 1
			if(heapT[p] <= heapT[k]) break
			;[heapT[p], heapT[k]] = [heapT[k], heapT[p]]
			;[heapI[p], heapI[k]] = [heapI[k], heapI[p]]
			k = p
		}
	}
	const pop = (): [number, number] => {
		const t = heapT[0], i = heapI[0]
		const lt = heapT.pop()!, li = heapI.pop()!
		if(heapT.length > 0) {
			heapT[0] = lt; heapI[0] = li
			let k = 0
			for(;;) {
				const a = 2 * k + 1, b = a + 1
				let m = k
				if(a < heapT.length && heapT[a] < heapT[m]) m = a
				if(b < heapT.length && heapT[b] < heapT[m]) m = b
				if(m === k) break
				;[heapT[m], heapT[k]] = [heapT[k], heapT[m]]
				;[heapI[m], heapI[k]] = [heapI[k], heapI[m]]
				k = m
			}
		}
		return [t, i]
	}
	const start = sj * nx + si
	time[start] = 0
	push(0, start)
	while(heapT.length > 0) {
		const [t, idx] = pop()
		if(done[idx]) continue
		done[idx] = 1
		const i = idx % nx, j = (idx - i) / nx
		for(let dj = -1; dj <= 1; dj++) {
			for(let di = -1; di <= 1; di++) {
				if(di === 0 && dj === 0) continue
				const ni = i + di, nj = j + dj
				if(ni < 0 || ni >= nx || nj < 0 || nj >= nz) continue
				const nidx = nj * nx + ni
				if(done[nidx] || heights[nidx] > 0) continue
				const distM = Math.hypot(di * dxKm, dj * dzKm) * 1000
				const v = 0.5 * (speed(idx) + speed(nidx))
				const nt = t + distM / v
				if(nt < time[nidx]) {
					time[nidx] = nt
					push(nt, nidx)
				}
			}
		}
	}
	return time
}
